import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import {AgGridReact} from "ag-grid-react";
import AgGridUtils from "../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import "react-datepicker/dist/react-datepicker.css";

import {useQuery} from "@tanstack/react-query";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import InputForm from "../../../../components/input/input-form.jsx";
import adrCodeService from "../../../../services/web/adr-code-service.js";
import AdrCodeCellRenderer from "../../../../components/adr-code-cell-renderer/adr-code-cell-renderer.jsx";

const initialFilters = {
    code: null,
    description: null,
}

const AdrCodeList = () => {

    const navigate = navigateHook();

    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState(initialFilters);

    const {data, isLoading: isSearching} = useQuery({
        queryFn: () => {
            return adrCodeService.list(filter)
        },
        queryKey: ["list", {filter}]
    });

    const apply = useCallback(() => {

        setFilter({
                "code": adrCodeService.normalizeAdrCode(filterValues?.code),
                "description": filterValues?.description,
            }
        );

    }, [filterValues])

    const clear = () => {
        setFilterValues(initialFilters)
        setFilter(initialFilters)
    };

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    const onChange = ({target}) => {
        let {id, value} = target;
        if(id === "code") {
            value = adrCodeService.formatAdrCode(value);
        }
        setFilterValues(prev => ({...prev, [id]: value}));
    }

    const onCellClicked = ({ data, event }) => {
        if(!data?.id) return;

        let urlDev = `/adr-code/form/${data.id}`;
        let isBlank = !!event.ctrlKey;

        navigate(urlDev, isBlank);
    };

    const gridRef = useRef(null);

    const columnDefs = useMemo(
        () => [
            {
                headerName: "Painel de segurança",
                field: "code",
                cellRenderer: AdrCodeCellRenderer,
                comparator: AgGridUtils.nullLastComparator,
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }
            },
            {
                headerName: "Descrição",
                field: "description",
                flex: 3,
                comparator: AgGridUtils.nullLastComparator,
            }
        ],
        []
    );

    const newAdrCode = () => {
        const urlDev = "/adr-code/form";
        navigate(urlDev);
    };

    return (
        <Template isLoading={isSearching}>
            <Template.Header>
                <Template.Title>Painel de segurança</Template.Title>
                <Button onClick={newAdrCode}>
                    <FaPlus /> Adicionar painel de segurança
                </Button>
            </Template.Header>
            <Template.Body className={`p-1 flex flex-col gap-2`}>
                <Card>
                    <Card.Body>
                        <div className="flex gap-4">
                            <InputForm
                                id="code"
                                label="Painel de segurança"
                                classNameWrapper="flex-1"
                                value={filterValues?.code ?? ""}
                                onChange={onChange}
                            />
                            <InputForm
                                id="description"
                                label="Descrição"
                                classNameWrapper="flex-1"
                                value={filterValues?.description ?? ""}
                                onChange={onChange}
                            />
                            <div className="flex gap-2 items-end">
                                <Button
                                    target="primary-outline"
                                    title="Limpar"
                                    onClick={clear}
                                >
                                    <FaEraser/>
                                </Button>
                                <Button
                                    title="Buscar"
                                    onClick={apply}
                                >
                                    <FaSearch/>
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card className="flex-1 min-h-0">
                    <Card.Body className="h-full w-full min-h-0">
                        <div className="h-[calc(100vh-153px)]  w-full min-h-0 aggrid-row-pointer">
                            <AgGridReact
                                ref={gridRef}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                rowHeight={58}
                                pagination
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                suppressCellFocus={true}
                                onCellClicked={onCellClicked}
                                animateRows={false}
                                gridOptions={{ localeText: AgGridUtils.localization() }}
                                theme={AgGridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AdrCodeList;
